/**
 * AppLoader - Shows loading UI while app contexts initialize
 *
 * Displays a branded loading screen during initial data fetching
 * (translations, branding, auth) to prevent flash of uninitialized content.
 */

import { ReactNode } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { config as productConfig } from '@/src/config';

interface AppLoaderProps {
  children: ReactNode;
}

export function AppLoader({ children }: AppLoaderProps) {
  const { isInitialized, isLoading } = useAppTranslation();
  const isTriveny = productConfig.isTriveny;

  // Show loading state while initializing
  if (!isInitialized) {
    // Product-aware splash. Previously hardcoded a blue "W" + "WeaveIn"
    // wordmark which flashed on Triveny app refresh before the React
    // bundle's actual logo took over — user reported the WeaveIn W
    // pre-loading. Triveny tenants now see their logo + paper bg.
    return (
      <div
        className={
          isTriveny
            ? 'fixed inset-0 flex items-center justify-center bg-[#fafaf7]'
            : 'fixed inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100'
        }
      >
        <div className="text-center space-y-4">
          {/* Logo/Brand */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {isTriveny ? (
              <img
                src="/triveny/logo.svg"
                alt="triveny"
                className="h-10 w-auto"
                ref={(el) => {
                  if (!el) return;
                  const isDark = document.documentElement.classList.contains('dark');
                  el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/triveny/wordmark.svg';
                }}
              />
            ) : (
              <>
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">W</span>
                </div>
                <span className="text-2xl font-semibold text-gray-800">WeaveIn</span>
              </>
            )}
          </div>

          {/* Spinner */}
          <LoadingSpinner size="lg" />

          {/* Loading text */}
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Optional: show subtle loading indicator while refreshing data
  if (isLoading) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 h-1 bg-blue-100 z-50 overflow-hidden">
          <div className="h-full bg-blue-600 animate-pulse" style={{ width: '100%' }} />
        </div>
        {children}
      </>
    );
  }

  return <>{children}</>;
}
